import fromPairs from 'lodash/fromPairs'
import isString from 'lodash/isString'
import { STRIPE_CANCELED_STATUS, STRIPE_SETTLED_STATUSES } from '@klear/stripe'
import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE } from '@klear/bank-account/constants'

export const PAYMENT_NETWORK_KLEAR_TO_KLEAR = 'klear_to_klear' as const
export const PAYMENT_NETWORK_TYPES = [
  PAYMENT_NETWORK_ACH,
  PAYMENT_NETWORK_WIRE,
  PAYMENT_NETWORK_KLEAR_TO_KLEAR
] as const
export type PaymentNetwork = (typeof PAYMENT_NETWORK_TYPES)[number]

export const PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT = 'klearFinancialAccount' as const
export const PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT = 'externalFinancialAccount' as const
export const PAYMENT_ACCOUNT_TYPE_PLACEHOLDER = 'placeholder' as const
export const SENDABLE_PAYMENT_ACCOUNT_TYPES = [
  PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
  PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT
] as const
export const PAYMENT_ACCOUNT_TYPES = [...SENDABLE_PAYMENT_ACCOUNT_TYPES, PAYMENT_ACCOUNT_TYPE_PLACEHOLDER] as const
export type PaymentAccountType = (typeof PAYMENT_ACCOUNT_TYPES)[number]

export type SendablePaymentAccountType = (typeof SENDABLE_PAYMENT_ACCOUNT_TYPES)[number]

export function isSendablePaymentAccountType(value: unknown): value is SendablePaymentAccountType {
  return isString(value) && (SENDABLE_PAYMENT_ACCOUNT_TYPES as readonly string[]).includes(value)
}

export const PAYMENT_TYPE_SUPPLIER = 'supplier' as const
export const PAYMENT_TYPE_TRANSFER = 'transfer' as const
export const PAYMENT_TYPE_BUFFER_FEES = 'buffer_fees' as const
export const PAYMENT_TYPE_LATE_FEES = 'late_fees' as const
export const PAYMENT_TYPE_SUBSCRIPTION_FEES = 'subscription_fees' as const
export const PAYMENT_TYPE_PAAS = 'paas' as const
export const PAYMENT_TYPE_COLLECTION_TRANSFER = 'collection_transfer' as const
export const PAYMENT_TYPE_CASH_BACK = 'cash_back' as const
export const PAYMENT_TYPE_PARTICIPATORY_FEES = 'participatory_fees' as const
export const PAYMENT_TYPE_COLLECTION_FEE = 'collection_fee' as const
export const PAYMENT_TYPE_COLLECTION_RECOVERY = 'collection_recovery' as const
export const PAYMENT_TYPE_DISBURSEMENT_FEE = 'disbursement_fee' as const
export const PAYMENT_TYPE_DISBURSEMENT_ADVANCE = 'disbursement_advance' as const
export const PAYMENT_TYPE_DISBURSEMENT_PAAS = 'disbursement_paas' as const
export const PAYMENT_TYPE_COLLECTION_PAAS = 'collection_paas' as const
export const PAYMENT_TYPE_BUY_OUT = 'buy_out' as const
export const PAYMENT_TYPE_REFUND = 'refund' as const

export const PAYMENT_FEE_ENTITY_TYPES = [PAYMENT_TYPE_BUFFER_FEES, PAYMENT_TYPE_LATE_FEES]

export const PAYMENT_LINE_ITEM_TYPE_PURCHASE = 'purchase' as const
export const PAYMENT_LINE_ITEM_TYPE_MONEX_FEE = 'monex_fee' as const
export const PAYMENT_LINE_ITEM_TYPE_FX_SPREAD = 'fx_spread' as const
export const PAYMENT_LINE_ITEM_TYPE_WIRE_FEE = 'wire_fee' as const
export const PAYMENT_LINE_ITEM_TYPE_CORRECTION = 'correction' as const

export const PAYMENT_LINE_ITEM_TYPES = [
  PAYMENT_LINE_ITEM_TYPE_PURCHASE,
  PAYMENT_LINE_ITEM_TYPE_MONEX_FEE,
  PAYMENT_LINE_ITEM_TYPE_FX_SPREAD,
  PAYMENT_LINE_ITEM_TYPE_WIRE_FEE,
  PAYMENT_LINE_ITEM_TYPE_CORRECTION
] as const
export type PaymentLineItemType = (typeof PAYMENT_LINE_ITEM_TYPES)[number]

export const PAYMENT_LINE_ITEM_TYPE_LABELS = {
  [PAYMENT_LINE_ITEM_TYPE_PURCHASE]: 'Purchase',
  [PAYMENT_LINE_ITEM_TYPE_MONEX_FEE]: 'Monex fee',
  [PAYMENT_LINE_ITEM_TYPE_FX_SPREAD]: 'FX spread',
  [PAYMENT_LINE_ITEM_TYPE_WIRE_FEE]: 'Wire fee',
  [PAYMENT_LINE_ITEM_TYPE_CORRECTION]: 'Correction'
} as const satisfies Record<PaymentLineItemType, string>

export const PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT = 'order_event' as const
export const PAYMENT_LINE_ITEM_SOURCE_ITEM = 'item' as const
export const PAYMENT_LINE_ITEM_SOURCES = [PAYMENT_LINE_ITEM_SOURCE_ORDER_EVENT, PAYMENT_LINE_ITEM_SOURCE_ITEM] as const

export const FEE_COLLECTION_ALLOCATION_PAYMENT_TYPES = [
  PAYMENT_TYPE_COLLECTION_FEE,
  ...PAYMENT_FEE_ENTITY_TYPES
] as const

export const PAYMENT_TYPES = [
  PAYMENT_TYPE_SUPPLIER,
  PAYMENT_TYPE_TRANSFER,
  PAYMENT_TYPE_BUFFER_FEES,
  PAYMENT_TYPE_LATE_FEES,
  PAYMENT_TYPE_SUBSCRIPTION_FEES,
  PAYMENT_TYPE_PAAS,
  PAYMENT_TYPE_CASH_BACK,
  PAYMENT_TYPE_PARTICIPATORY_FEES,
  PAYMENT_TYPE_COLLECTION_FEE,
  PAYMENT_TYPE_COLLECTION_RECOVERY,
  PAYMENT_TYPE_COLLECTION_TRANSFER,
  PAYMENT_TYPE_DISBURSEMENT_FEE,
  PAYMENT_TYPE_DISBURSEMENT_ADVANCE,
  PAYMENT_TYPE_DISBURSEMENT_PAAS,
  PAYMENT_TYPE_COLLECTION_PAAS,
  PAYMENT_TYPE_BUY_OUT,
  PAYMENT_TYPE_REFUND
] as const
export type PaymentType = (typeof PAYMENT_TYPES)[number]

export function isPaymentType(value: unknown): value is PaymentType {
  return isString(value) && (PAYMENT_TYPES as readonly string[]).includes(value)
}

const UNWRITTEN_PAYMENT_TYPES = [PAYMENT_TYPE_BUY_OUT, PAYMENT_TYPE_REFUND] as const

export const WRITTEN_PAYMENT_TYPES = PAYMENT_TYPES.filter(
  (type) => !(UNWRITTEN_PAYMENT_TYPES as readonly PaymentType[]).includes(type)
) as readonly Exclude<PaymentType, (typeof UNWRITTEN_PAYMENT_TYPES)[number]>[]

export const PAYMENT_TYPE_READABLE_NAMES = {
  [PAYMENT_TYPE_DISBURSEMENT_ADVANCE]: 'Invoice funding',
  [PAYMENT_TYPE_BUFFER_FEES]: 'Buffer Fee Payment',
  [PAYMENT_TYPE_LATE_FEES]: 'Late Fee Payment',
  [PAYMENT_TYPE_SUBSCRIPTION_FEES]: 'Subscription Fee Payment',
  [PAYMENT_TYPE_TRANSFER]: 'Klear internal transfer',
  [PAYMENT_TYPE_SUPPLIER]: 'Supplier Payment',
  [PAYMENT_TYPE_PAAS]: 'PAAS Payment',
  [PAYMENT_TYPE_CASH_BACK]: 'Cash Back',
  [PAYMENT_TYPE_PARTICIPATORY_FEES]: 'Participatory Fees Payment',
  [PAYMENT_TYPE_COLLECTION_FEE]: 'Fee Collection',
  [PAYMENT_TYPE_COLLECTION_RECOVERY]: 'Advance recovery (inbound)',
  [PAYMENT_TYPE_COLLECTION_TRANSFER]: 'Collection funding (inbound ACH)',
  [PAYMENT_TYPE_DISBURSEMENT_FEE]: 'Fee Disbursement',
  [PAYMENT_TYPE_DISBURSEMENT_PAAS]: 'PAAS Disbursement',
  [PAYMENT_TYPE_COLLECTION_PAAS]: 'PAAS Collection',
  [PAYMENT_TYPE_BUY_OUT]: 'Position buy-out',
  [PAYMENT_TYPE_REFUND]: 'Refund'
} satisfies Record<PaymentType, string>

export const PaymentTypeOptions = [
  { value: PAYMENT_TYPE_DISBURSEMENT_ADVANCE, label: 'Fund Invoice Offer (Outbound)' },
  { value: PAYMENT_TYPE_LATE_FEES, label: 'Late Fee Payment (Inbound)' },
  { value: PAYMENT_TYPE_SUBSCRIPTION_FEES, label: 'Subscription Fee Payment (Inbound)' },
  { value: PAYMENT_TYPE_TRANSFER, label: 'Klear internal transfer' },
  { value: PAYMENT_TYPE_SUPPLIER, label: 'Supplier Payment' },
  { value: PAYMENT_TYPE_CASH_BACK, label: 'Cash Back' },
  { value: PAYMENT_TYPE_PARTICIPATORY_FEES, label: 'Participatory Fees' },
  { value: PAYMENT_TYPE_COLLECTION_FEE, label: 'Fee Collection' },
  { value: PAYMENT_TYPE_COLLECTION_RECOVERY, label: 'Advance recovery (inbound)' },
  { value: PAYMENT_TYPE_DISBURSEMENT_FEE, label: 'Fee Disbursement' },
  { value: PAYMENT_TYPE_DISBURSEMENT_PAAS, label: 'PAAS Disbursement (Outbound)' },
  { value: PAYMENT_TYPE_COLLECTION_PAAS, label: 'PAAS Collection (Inbound)' }
]

export const PaymentNetworkOptions = [
  { label: 'Wire US Domestic', value: PAYMENT_NETWORK_WIRE },
  { label: 'ACH', value: PAYMENT_NETWORK_ACH },
  { label: 'Klear to Klear ⚡️ (instant)', value: PAYMENT_NETWORK_KLEAR_TO_KLEAR }
]

export const EXTERNAL_PAYMENT_PROVIDERS = ['truist', 'monex', 'wire', 'check', 'ach', 'manual'] as const
export type ExternalPaymentProvider = (typeof EXTERNAL_PAYMENT_PROVIDERS)[number]

export const EXTERNAL_PAYMENT_PROVIDER_LABELS: Record<ExternalPaymentProvider, string> = {
  truist: 'Truist',
  monex: 'Monex',
  wire: 'Wire Transfer',
  check: 'Check',
  ach: 'ACH',
  manual: 'Manual'
}

export const STRIPE_ID_PREFIXES = ['obp_', 'ibt_', 'obt_', 'pi_']

export function externalPaymentId(provider: string, reference?: string): string {
  const sanitized = provider.toLowerCase().replace(/[^a-z0-9]/g, '')
  return reference ? `${sanitized}_${reference}` : `${sanitized}_none`
}

export const PAYMENT_STATUS_DRAFT = 'draft' as const
export const PAYMENT_STATUS_SCHEDULED = 'scheduled' as const
export const PAYMENT_STATUS_PROCESSING = 'processing' as const
export const PAYMENT_STATUS_SETTLED = 'settled' as const
export const PAYMENT_STATUS_FAILED = 'failed' as const
export const PAYMENT_STATUS_CANCELED = 'canceled' as const
export const PAYMENT_STATUS_RETURNED = 'returned' as const

const FOREIGN_STATUS_MAP: Readonly<Record<string, PaymentStatus>> = {
  ...fromPairs(STRIPE_SETTLED_STATUSES.map((status) => [status, PAYMENT_STATUS_SETTLED])),
  [STRIPE_CANCELED_STATUS]: PAYMENT_STATUS_CANCELED
}

export const PAYMENT_STATUS_TYPES = [
  PAYMENT_STATUS_DRAFT,
  PAYMENT_STATUS_SCHEDULED,
  PAYMENT_STATUS_PROCESSING,
  PAYMENT_STATUS_SETTLED,
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_CANCELED,
  PAYMENT_STATUS_RETURNED
] as const

export type PaymentStatus = (typeof PAYMENT_STATUS_TYPES)[number]

export const SETTLED_PAYMENT_STATUSES = [PAYMENT_STATUS_SETTLED] as const satisfies readonly PaymentStatus[]

export const IN_TRANSIT_PAYMENT_STATUSES = [PAYMENT_STATUS_PROCESSING] as const satisfies readonly PaymentStatus[]

export const COMMITTED_PAYMENT_STATUSES = [
  ...SETTLED_PAYMENT_STATUSES,
  ...IN_TRANSIT_PAYMENT_STATUSES
] as const satisfies readonly PaymentStatus[]

export const TERMINAL_FAILURE_PAYMENT_STATUSES = [
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_CANCELED,
  PAYMENT_STATUS_RETURNED
] as const satisfies readonly PaymentStatus[]

export const EDITABLE_PAYMENT_STATUSES = [
  PAYMENT_STATUS_DRAFT,
  PAYMENT_STATUS_SCHEDULED,
  PAYMENT_STATUS_FAILED
] as const satisfies readonly PaymentStatus[]

export function isKnownPaymentStatus(raw: string | null | undefined): boolean {
  if (raw == null) return false
  const value = raw.trim().toLowerCase()
  return (PAYMENT_STATUS_TYPES as readonly string[]).includes(value) || value in FOREIGN_STATUS_MAP
}

export function normalizePaymentStatus(raw: string | null | undefined): PaymentStatus {
  if (raw == null) return PAYMENT_STATUS_DRAFT
  const value = raw.trim().toLowerCase()
  if ((PAYMENT_STATUS_TYPES as readonly string[]).includes(value)) return value as PaymentStatus
  return FOREIGN_STATUS_MAP[value] ?? PAYMENT_STATUS_DRAFT
}

export const PAYMENT_DOCUMENT_LIST_MAX_IDS = 100
